import React, { useMemo } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CheckIcon, XIcon } from "lucide-react";
import type { AnswerState } from "../../types/exercises";
import type { LessonPhase } from "../../hooks/useLessonRunner";
import { BrutalButton } from "../ui/BrutalButton";
import { spring } from "../../utils/motion";
interface CheckBarProps {
  phase: LessonPhase;
  answer: AnswerState;
  skipped: boolean;
  onCheck: () => void;
  onSkip: () => void;
  onContinue: () => void;
}
const praise = ["Nice work!", "¡Muy bien!", "Spot on.", "¡Perfecto!", "You've got it."];
export function CheckBar({
  phase,
  answer,
  skipped,
  onCheck,
  onSkip,
  onContinue
}: CheckBarProps) {
  const reduce = useReducedMotion() ?? false;
  const checked = phase === "checked";
  const cheer = useMemo(() => praise[Math.floor(Math.random() * praise.length)], [phase]);
  const correct = checked && answer.correct;
  const bg = !checked ? "bg-surface" : correct ? "bg-good-tint" : "bg-bad-tint";
  return <div className={`fixed inset-x-0 bottom-0 z-40 border-t-3 border-line transition-colors duration-200 ${bg}`} data-id="element-273" data-mp="v01exl5qqscr">
      <div className="mx-auto flex min-h-[112px] max-w-3xl flex-col gap-4 px-4 py-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-274" data-mp="q78ayzi3jo1q">
        <AnimatePresence mode="wait" initial={false} data-id="element-275" data-mp="h7v2a91sr3i2">
          {checked ? <motion.div key="feedback" initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={spring.smooth} className="flex min-w-0 items-center gap-4" role="status" aria-live="assertive" data-id="element-276" data-mp="h5s2ixaudw05">
              <motion.span initial={reduce ? false : {
            scale: 0.6,
            rotate: -12
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={spring.bouncy} className={`grid h-12 w-12 shrink-0 place-items-center rounded-full border-3 border-line ${correct ? "bg-good" : "bg-bad"} text-night`} data-id="element-277" data-mp="zy2z69ewkxpt">
                {correct ? <CheckIcon className="h-6 w-6" strokeWidth={3.5} data-id="element-278" data-mp="3r3sk1qnhfawd" /> : <XIcon className="h-6 w-6" strokeWidth={3.5} data-id="element-279" data-mp="3r2hbzen07gzg" />}
              </motion.span>
              <div className="min-w-0" data-id="element-280" data-mp="3d7f0mbze0zf9">
                <p className="text-xl font-bold text-ink" data-id="element-281" data-mp="3h5wa326a6lzu">
                  {correct ? cheer : skipped ? "Skipped — here's the answer" : "Not quite"}
                </p>
                {!correct && answer.solution && <p className="mt-0.5 text-[15px] text-ink" data-id="element-282" data-mp="386rartmwaomm">
                    <span className="font-semibold" data-id="element-283" data-mp="2thleoejcteiy">Answer:</span> {answer.solution}
                  </p>}
              </div>
            </motion.div> : <motion.div key="skip" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="hidden sm:block" data-id="element-284" data-mp="2kgetvt9o9hjw">
              <BrutalButton variant="surface" onClick={onSkip} data-id="element-285" data-mp="2pegcye73lgvd">
                Skip
              </BrutalButton>
            </motion.div>}
        </AnimatePresence>

        <div className="flex gap-3 sm:w-auto" data-id="element-286" data-mp="20uv4y6my6hkf">
          {!checked && <span className="sm:hidden" data-id="element-287" data-mp="20wy3jf261j0r">
              <BrutalButton variant="surface" onClick={onSkip} data-id="element-288" data-mp="2eumoemw8gq5t">
                Skip
              </BrutalButton>
            </span>}
          <div className="flex-1 sm:w-44 sm:flex-none" data-id="element-289" data-mp="2arhqkxn3lcni">
            {checked ? <BrutalButton variant={correct ? "good" : "bad"} full onClick={onContinue} size="lg" data-id="element-290" data-mp="2oqzj9o3ila25">
                Continue
              </BrutalButton> : <BrutalButton full onClick={onCheck} disabled={!answer.ready} size="lg" data-id="element-291" data-mp="2yin4365kfsun">
                Check
              </BrutalButton>}
          </div>
        </div>
      </div>
    </div>;
}